import Vue from 'vue'
import './plugins/vuetify'
import App from './App.vue'
import router from './router'
import { store } from './store/index'
// import './registerServiceWorker'
import VueResource from 'vue-resource'
import VueNativeSock from 'vue-native-websocket'
import Notifications from 'vue-notification'
import VueApexCharts from 'vue-apexcharts'

import './styles/common.scss'
// KAN-95 暗室暗色主題：token 與覆寫層必須在 common.scss「之後」import，
// source order 讓 .theme-dark 覆寫勝出（配合特異度）
import './styles/theme-tokens.scss'
import './styles/theme-dark.scss'
import './styles/components/tables.scss'
import { applyThemeClass } from './utils/theme'
import {
  applyDocumentLang,
  isSupportedLocale,
  persistLocale,
  resolveInitialLocale,
} from './utils/locale'
import './plugins/date-range-picker'
import './filters/date-filter'
import { dbServices } from '@/services/db'
import { i18n } from './locale'

Vue.use(VueResource)
Vue.use(VueApexCharts)

Vue.use(Notifications)

Vue.use(VueNativeSock, 'ws://localhost:8052', {
  format: 'S16LE',
  connectManually: true,
})

Vue.config.productionTip = false

Vue.http.options.xhr = {
  withCredentials: true,
}
Vue.http.options.emulateJSON = true

Vue.component('apexchart', VueApexCharts)

Vue.config.errorHandler = function (err, vm, info) {
  console.log(err)
  console.log(vm)
  console.log(info)
}

/**
 * 啟動流程：先 fetch 醫院別 runtime config，再 mount Vue
 * 這確保 i18n locale 和 feature flags 在第一次 render 就正確
 * 若 fetch 失敗，使用預設值（backward compat：等同 HOSPITAL=default）
 */
async function bootstrap() {
  // KAN-95 暗室暗色主題：mount 前先把 root class 套上（state.ui.theme 已於 store 初始化解析），
  // 避免第一次 render 亮底閃一下才轉暗
  applyThemeClass(store.state.ui.theme)

  // Step 1: 載入醫院別 runtime 配置
  let hospitalDefaultLocale = null
  try {
    const resp = await fetch('/api/config/runtime')
    if (resp.ok) {
      const hospitalConfig = await resp.json()
      store.dispatch('setHospital', hospitalConfig)

      // 醫院配置的語系只是「這台機器沒人選過時的預設」，實際採用與否由
      // resolveInitialLocale 決定——使用者手動選過的語言優先權更高。
      if (hospitalConfig.locale && hospitalConfig.locale.default) {
        hospitalDefaultLocale = hospitalConfig.locale.default
      }
      console.log('[bootstrap] 醫院配置載入成功：', hospitalConfig.hospital_id)
    } else {
      console.warn('[bootstrap] /api/config/runtime 回傳非 200，使用預設配置')
    }
  } catch (err) {
    // fetch 失敗（server 尚未啟動 / 網路問題）→ 使用預設值，不阻擋 mount
    console.warn('[bootstrap] 醫院配置載入失敗，使用預設值：', err.message)
  }

  // Step 2: mount 前載入 client runtime setting。Exam2DetailPage 在 created/mounted
  // 立即解析 KKASR endpoint 並建立 client；若仍沿用根元件 created 後才非同步 fetch，
  // 直接開 /exam2 時幾乎一定先吃到空 setting，明確 runtime URL 便形同無效。
  try {
    const res = await dbServices.get_setting()
    if (res && res.success && res.data) {
      const clientSetting = res.data
      await store.dispatch('setSetting', clientSetting)
    } else {
      console.warn('[bootstrap] /api/setting 無有效配置，KKASR 使用 host-aware fallback')
    }
  } catch (err) {
    console.warn('[bootstrap] /api/setting 載入失敗，KKASR 使用 host-aware fallback：', err.message)
  }

  // Step 3: 決定介面語系。這段刻意放在 try/catch 外面——醫院配置抓不到時
  // 使用者先前選的語言仍然要生效，不能整段被 fetch 失敗吃掉。
  const initialLocale = resolveInitialLocale(hospitalDefaultLocale)
  i18n.locale = initialLocale
  applyDocumentLang(initialLocale)

  // Step 4: mount Vue
  const vm = new Vue({
    i18n,
    router,
    store,
    render: (h) => h(App),
    methods: {
      /**
       * 全域語系切換的唯一入口（App.vue 的地球選單呼叫這裡）。
       *
       * 三件事必須一起做，少任何一件都是使用者看得到的 bug：
       *   1. $i18n.locale ── 換掉畫面上的字
       *   2. persistLocale ── 不存的話重整就跳回醫院預設，醫師在檢查中途
       *      按到重整會突然變回中文
       *   3. applyDocumentLang ── 同步 <html lang>，螢幕閱讀器與瀏覽器
       *      翻譯提示都看這個屬性
       */
      set_locale(locale) {
        if (!isSupportedLocale(locale)) return
        this.$i18n.locale = locale
        persistLocale(locale)
        applyDocumentLang(locale)
      }
    },
  }).$mount('#app')
}

bootstrap()
